// Match: turf-war rules, lifecycle (intro → countdown → play → time's up → judge → results), team setup.
import * as THREE from 'three';
import { G, emit, on, clamp } from '../core/ctx.js';
import { MATCH, PLAYER, WEAPON_ORDER, SUB_ORDER, SPECIAL_ORDER, BOT_NAMES, TRAINING_BOT_NAMES, TEAM_NAMES, ZONES, SLIME_COLORS } from '../config.js';
import { ZoneControl } from './zones.js';
import { randomStyle } from './character-style.js';
import { MORPH_IDS } from './character-geo.js';
import { Actor } from './actor.js';
import { BotBrain } from './bots.js';
import { PlayerController } from './player.js';
import { BossMode, BOSS_MODE } from '../boss/bossMode.js';

const _v = new THREE.Vector3(), _up = new THREE.Vector3(0, 1, 0);

export class Match {
  constructor(opts) {
    this.opts = opts;          // { duration, difficulty, attract, practice, playerName, weapon, CharacterClass, input, rig, mode }
    this.attract = !!opts.attract;
    this.practice = !!opts.practice;   // solo on an empty stage: no enemies, no teammates, no clock
    // turf | zones (Zone Control) | boss (one squad, team 0, vs HULLBREAKER); attract backdrops and practice are turf
    this.mode = this.attract || this.practice ? 'turf' : opts.mode === 'boss' ? 'boss' : opts.mode === 'zones' ? 'zones' : opts.mode === 'duel' ? 'duel' : opts.mode === 'deathmatch' ? 'deathmatch' : 'turf';
    // 1v1 Duel rules: 'first5' = first to 5 splats (or most at the buzzer); 'deathmatch' = no cap, most splats when time's up
    this.duelRule = this.mode === 'duel' && opts.duelRule === 'deathmatch' ? 'deathmatch' : 'first5';
    this.duration = opts.duration || (this.mode === 'zones' ? ZONES.duration : this.mode === 'boss' ? BOSS_MODE.duration : this.mode === 'deathmatch' ? 180 : MATCH.defaultDuration);
    this.zones = null;             // ZoneControl (Zone Control mode)
    this.time = this.duration;
    this.state = 'init';
    this.stateT = 0;
    this.actors = [];
    this.controller = null;
    this.result = null;
    this.paused = false;
    this.lastMinuteFired = false;
    this.lastCount = 99;
    this.events = [];
  }

  playing() { return this.state === 'playing' && !this.paused; }
  canRespawn() { return this.state === 'playing'; }

  setup() {
    const o = this.opts;
    const CharacterClass = o.CharacterClass;
    if (o.roster) { this._setupRoster(o, CharacterClass); return; }
    // weapons: each team gets a balanced mix
    const pickTeam = (first) => {
      const pool = [...WEAPON_ORDER];
      const out = [];
      if (first) { out.push(first); pool.splice(pool.indexOf(first), 1); }
      while (out.length < MATCH.teamSize) {
        if (!pool.length) pool.push(...WEAPON_ORDER);
        out.push(pool.splice((Math.random() * pool.length) | 0, 1)[0]);
      }
      return out;
    };
    // PRACTICE (an offline match you play): BOT-tagged training bots. Every bot (the menu backdrop's too) rolls its own
    // random look and morph animal
    const training = !this.attract;
    const names = shuffle([...(training ? TRAINING_BOT_NAMES : BOT_NAMES)]);
    let ni = 0;
    const botName = () => (training ? 'BOT ' : '') + names[ni++ % names.length];
    const botStyle = () => randomStyle();
    const botMorph = () => MORPH_IDS[(Math.random() * MORPH_IDS.length) | 0];
    const noBots = !!o.noBots;
    if (this.mode === 'deathmatch') {
      this.deathmatchSpawns = this._getDeathmatchSpawns(8);
      const savedProf = (() => { try { return typeof localStorage !== 'undefined' ? JSON.parse(localStorage.getItem('slimeduel.profile') || '{}') : {}; } catch { return {}; } })();
      const playerChosenHex = String(o.slimeColor || savedProf.slimeColor || (G.teamColors?.[0] ? ('#' + (G.teamColors[0].isColor ? G.teamColors[0].getHexString() : new THREE.Color(G.teamColors[0]).getHexString())) : '#a6f01a')).toLowerCase();

      const foundIdx = SLIME_COLORS.findIndex(c => c.hex.toLowerCase() === playerChosenHex);
      const playerColorHex = foundIdx >= 0 ? SLIME_COLORS[foundIdx].hex : playerChosenHex;
      const otherColors = SLIME_COLORS.filter((_, i) => i !== foundIdx);

      const ffaHexes = [
        playerColorHex,
        ...otherColors.slice(0, 7).map(c => c.hex),
      ];
      while (ffaHexes.length < 8) {
        ffaHexes.push('#a6f01a');
      }
      const FFA_COLORS = ffaHexes.map(h => new THREE.Color(h));
      G.teamColors = FFA_COLORS;
      G.teamHex = ffaHexes;
      G.ffaColors = FFA_COLORS;
      G.ffaHex = ffaHexes;

      const weapons = pickTeam(o.weapon);
      weapons.push(...pickTeam(null));
      const targetCount = 8;
      for (let s = 0; s < targetCount; s++) {
        const isLocal = s === 0 && !this.attract;
        if (noBots && !isLocal) continue;
        const sub = isLocal ? o.sub : Math.random() < 0.5 ? null : SUB_ORDER[(Math.random() * SUB_ORDER.length) | 0];
        const special = isLocal ? o.special : Math.random() < 0.5 ? null : SPECIAL_ORDER[(Math.random() * SPECIAL_ORDER.length) | 0];
        const savedMorph = (() => { try { return typeof localStorage !== 'undefined' ? (JSON.parse(localStorage.getItem('slimeduel.profile') || '{}').morph || null) : null; } catch { return null; } })();
        const rawLocalMorph = o.morph && o.morph !== 'squid' ? o.morph : (savedMorph || o.morph || G.game?.profile?.morph || 'squid');
        const localMorph = String(rawLocalMorph || 'squid').replace(/^morph_/, '').toLowerCase();
        const a = new Actor({
          team: s, slot: s, weapon: weapons[s] || 'shooter', sub, special, isLocal, isBot: !isLocal,
          name: isLocal ? (o.playerName || 'You') : botName(),
          style: isLocal ? { ...(o.style || {}) } : botStyle(), CharacterClass,
          morph: isLocal ? localMorph : botMorph(),
          ffaColor: FFA_COLORS[s],
        });
        a.colorHex = ffaHexes[s];
        G.scene.add(a.character.root);
        if ((!isLocal || o.autopilot) && !(noBots && !isLocal)) a.bot = new BotBrain(a, o.difficulty);
        this.actors.push(a);
      }
      G.actors = this.actors;
      this.local = this.actors.find((a) => a.isLocal) || null;
      G.local = this.local;
      if (this.local && !o.autopilot) this.controller = new PlayerController(this.local, o.rig, o.input);

      // Distribute initial spawns far apart across the arena
      for (let i = 0; i < this.actors.length; i++) {
        const a = this.actors[i];
        const sp = this.deathmatchSpawns[i % this.deathmatchSpawns.length];
        _v.set(sp.x, sp.y, sp.z);
        a.spawnAt(_v, sp.yaw);
        a.invuln = 0;
        if (a.bot) { a.bot.aimYaw = a.yaw; a.bot.aimPitch = 0; }
      }
      this.unsubs = [
        on('splatted', (e) => this._onSplatted(e)),
      ];
      return;
    }
    const boss = this.mode === 'boss';
    if (this.mode === 'duel') {
      const savedProf = (() => { try { return typeof localStorage !== 'undefined' ? JSON.parse(localStorage.getItem('slimeduel.profile') || '{}') : {}; } catch { return {}; } })();
      const playerChosenHex = String(o.slimeColor || savedProf.slimeColor || (G.teamColors?.[0] ? ('#' + (G.teamColors[0].isColor ? G.teamColors[0].getHexString() : new THREE.Color(G.teamColors[0]).getHexString())) : '#a6f01a')).toLowerCase();
      const foundIdx = SLIME_COLORS.findIndex(c => c.hex.toLowerCase() === playerChosenHex);
      const playerColorHex = foundIdx >= 0 ? SLIME_COLORS[foundIdx].hex : playerChosenHex;
      const otherColors = SLIME_COLORS.filter((_, i) => i !== foundIdx);
      const rivalColorHex = (otherColors[(Math.random() * otherColors.length) | 0] || { hex: '#ff3f9e' }).hex;

      const duelHexes = [playerColorHex, rivalColorHex];
      const DUEL_COLORS = duelHexes.map(h => new THREE.Color(h));
      G.teamColors = DUEL_COLORS;
      G.teamHex = duelHexes;
      G.ffaColors = DUEL_COLORS;
      G.ffaHex = duelHexes;
    }
    // humans-only stage (config noBots) offline: a match is just you (the ?devstage walk), the attract backdrop nobody
    // (o.mannequins: idle, brainless kids for the render audits)
    for (let team = 0; team < (boss || this.practice ? 1 : 2); team++) {
      const weapons = pickTeam(team === 0 && !this.attract ? o.weapon : null);
      if (boss) weapons.push(...pickTeam(null));   // the whole squad on one side: 8 kids, every weapon kind
      const targetTeamSize = this.practice ? 1 : boss ? BOSS_MODE.squad : this.mode === 'duel' ? 1 : MATCH.teamSize;
      for (let s = 0; s < targetTeamSize; s++) {
        const isLocal = team === 0 && s === 0 && !this.attract;
        if (noBots && !isLocal && !(this.attract && o.mannequins)) continue;
        // subs: yours from the loadout; bots carry a random one (about half keep their weapon's default)
        const sub = isLocal ? o.sub : Math.random() < 0.5 ? null : SUB_ORDER[(Math.random() * SUB_ORDER.length) | 0];
        const special = isLocal ? o.special : Math.random() < 0.5 ? null : SPECIAL_ORDER[(Math.random() * SPECIAL_ORDER.length) | 0];
        const savedMorph = (() => { try { return typeof localStorage !== 'undefined' ? (JSON.parse(localStorage.getItem('slimeduel.profile') || '{}').morph || null) : null; } catch { return null; } })();
        const rawLocalMorph = o.morph && o.morph !== 'squid' ? o.morph : (savedMorph || o.morph || G.game?.profile?.morph || 'squid');
        const localMorph = String(rawLocalMorph || 'squid').replace(/^morph_/, '').toLowerCase();
        const a = new Actor({
          team, slot: s, weapon: weapons[s], sub, special, isLocal, isBot: !isLocal,
          name: isLocal ? (o.playerName || 'You') : botName(),
          // your look from the Locker (an empty style resolves from your name); bots get random looks
          style: isLocal ? { ...(o.style || {}) } : botStyle(), CharacterClass,
          morph: isLocal ? localMorph : botMorph(),
          ffaColor: this.mode === 'duel' ? G.teamColors[team] : undefined,
        });
        if (this.mode === 'duel') {
          a.colorHex = G.teamHex[team];
          a.ffaColor = G.teamColors[team];
        }
        if (isLocal && o.style) { /* reserved for future customisation */ }
        G.scene.add(a.character.root);
        if ((!isLocal || o.autopilot) && !(noBots && !isLocal)) a.bot = new BotBrain(a, o.difficulty);
        this.actors.push(a);
      }
    }
    G.actors = this.actors;
    this.local = this.actors.find((a) => a.isLocal) || null;
    G.local = this.local;
    if (this.local && !o.autopilot) this.controller = new PlayerController(this.local, o.rig, o.input);
    // initial placement on the spawn decks (standing, no drop)
    for (const a of this.actors) {
      const pad = G.level.spawnPads[a.team];
      const ang = (a.slot / (this.mode === 'boss' ? BOSS_MODE.squad : 4)) * Math.PI * 2 + 0.6, rr = this.mode === 'boss' ? 1.7 : 1.2;
      _v.set(pad.x + Math.cos(ang) * rr, pad.y, pad.z + Math.sin(ang) * rr);
      a.spawnAt(_v, a.team === 0 ? 0 : Math.PI);
      a.invuln = 0;
      if (a.bot) { a.bot.aimYaw = a.yaw; a.bot.aimPitch = 0; }
    }
    this.unsubs = [
      on('splatted', (e) => this._onSplatted(e)),
    ];
    if (this.mode === 'zones') {
      this.zones = new ZoneControl(this);
      this.unsubs.push(on('turf', (e) => this._zoneTurf(e)));
    }
    if (this.mode === 'boss') { this.bossMode = new BossMode(this); this.boss = this.bossMode.boss; }
  }

  // Online: the host's roster — who owns which slime dualist (players their own, the host the bots).
  _setupRoster(o, CharacterClass) {
    const me = o.myId;
    this.follower = !o.host;
    for (const r of o.roster) {
      const mine = r.owner === me;
      const savedMorph = (() => { try { return typeof localStorage !== 'undefined' ? (JSON.parse(localStorage.getItem('slimeduel.profile') || '{}').morph || null) : null; } catch { return null; } })();
      // your own slime dualist: your saved animal; bots: the host's roll (older hosts: one picked from the name, so every
      // client agrees); other players: whatever they sent
      const nameMorph = () => { let x = 0; for (const ch of String(r.name || '')) x = (x * 31 + ch.charCodeAt(0)) >>> 0; return MORPH_IDS[x % MORPH_IDS.length]; };
      const rawMorph = mine && !r.bot ? (savedMorph || G.game?.profile?.morph || 'squid') : (r.morph || (r.bot ? nameMorph() : 'squid'));
      const cleanMorph = String(rawMorph || 'squid').replace(/^morph_/, '').toLowerCase();
      const a = new Actor({
        team: r.team, slot: r.slot, weapon: r.weapon, sub: r.sub || null, special: r.special || null,
        isLocal: mine && !r.bot, isBot: r.bot, name: r.name, style: r.style || undefined, CharacterClass,
        morph: cleanMorph, cos: mine || r.bot ? null : r.cos,
      });
      a.nid = r.nid; a.owner = r.owner; a.remote = !mine;
      G.scene.add(a.character.root);
      if (mine && (r.bot || o.autopilot)) a.bot = new BotBrain(a, o.difficulty);
      this.actors.push(a);
    }
    G.actors = this.actors;
    this.local = this.actors.find((a) => a.isLocal) || null;
    G.local = this.local;
    if (this.local && !o.autopilot) this.controller = new PlayerController(this.local, o.rig, o.input);
    for (const a of this.actors) {
      const pad = G.level.spawnPads[a.team];
      const ang = (a.slot / (this.mode === 'boss' ? BOSS_MODE.squad : 4)) * Math.PI * 2 + 0.6, rr = this.mode === 'boss' ? 1.7 : 1.2;
      _v.set(pad.x + Math.cos(ang) * rr, pad.y, pad.z + Math.sin(ang) * rr);
      a.spawnAt(_v, a.team === 0 ? 0 : Math.PI);
      a.invuln = 0;
      if (a.bot) { a.bot.aimYaw = a.yaw; a.bot.aimPitch = 0; }
    }
    this.unsubs = [on('splatted', (e) => this._onSplatted(e))];
    if (this.mode === 'zones') {   // Zone Control online: the host runs the rules, guests follow (zones.js netEvent)
      this.zones = new ZoneControl(this);
      this.unsubs.push(on('turf', (e) => this._zoneTurf(e)));
    }
    if (this.mode === 'boss') { this.bossMode = new BossMode(this); this.boss = this.bossMode.boss; }
  }

  // Zone Control: ink laid while standing on (or aiming into) the live zone counts as objective play (results / XP)
  _zoneTurf({ actor, area }) {
    const Z = this.zones;
    if (!Z || this.state !== 'playing' || !actor || !(area > 0)) return;
    const on = (p) => p && Z.active.zones.some((z) => inZone(z.def, p));
    if (on(actor.pos) || on(actor.aimPoint)) actor.stats.zoneTurf = (actor.stats.zoneTurf || 0) + area;
  }

  // Zone Control decided the match (knockout / overtime result): straight to time's up
  endZones(winner, reason) {
    this.zoneResult = { winner, reason };
    this.time = 0;
    if (this.zones) {
      this.zones.winner = winner;
      this.zones.reason = reason;
      this.zones.overtime = false;
    }
    if (this.state === 'playing') { this.setState('finish'); }
  }

  start() {
    this.setState(this.attract || this.practice ? 'playing' : 'intro');
  }

  setState(s) {
    this.state = s; this.stateT = 0;
    emit('match:state', { state: s, match: this });
  }

  dispose() {
    this.bossMode?.dispose(); this.bossMode = null; this.boss = null;
    for (const a of this.actors) { G.scene.remove(a.character.root); a.weaponRunner.reset(); a.character.dispose?.(); }
    this.unsubs?.forEach((u) => u());
    G.actors = [];
    G.local = null;
  }

  // Online, humans-only stage (config noBots): a player left, and nobody takes over their slime dualist — it bursts into its
  // own ink and is gone. HUD squads, the minimap, specials / weapons and the judge all read this.actors (G.actors is the
  // same array), so dropping it here is all they need.
  removeActor(a) {
    const i = this.actors.indexOf(a);
    if (i < 0) return;
    if (a.alive && a.character.root.visible) {
      _v.copy(a.pos); _v.y += 0.6;
      G.fx?.splatted(_v, a.color);
      G.fx?.burst?.(_v, _up, a.color, { count: 18, speed: 5, size: 0.1 });
      G.audio?.play?.('splat_big', { pos: a.pos, volume: 0.6 });
    }
    this.actors.splice(i, 1);
    if (G.rig?.spectate?.actor === a) G.rig.spectate.actor = null;   // a death cam watching them looks on at the spot
    G.scene.remove(a.character.root); a.weaponRunner.reset(); a.character.dispose?.();
    emit('actor:removed', { actor: a });
  }

  _onSplatted({ victim, attacker, cause }) {
    this.events.push({ t: this.duration - this.time, victim, attacker, cause });
    if (this.mode === 'duel' && this.state === 'playing') {
      if (attacker && attacker !== victim && (this.overtime || (this.duelRule === 'first5' && (attacker.stats?.splats || 0) >= 5))) {
        this.duelKnockout = !this.overtime;
        this.duelWinner = attacker;
        if (!this.follower) {
          this.setState('finish');
        }
      }
    }
    // Deathmatch sudden death: the first of the tied leaders to score takes the match
    if (this.mode === 'deathmatch' && this.state === 'playing' && this.overtime && this.tiedPlayers) {
      if (attacker && attacker !== victim && this.tiedPlayers.has(attacker) && !this.follower) this.setState('finish');
    }
  }

  update(dt) {
    if (this.paused) return;
    this.stateT += dt;
    switch (this.state) {
      case 'intro':
        if (this.stateT > (this.bossMode ? BOSS_MODE.intro : 4.2)) this.setState('playing');
        break;
      case 'playing': {
        if (this.practice) break;   // practice never runs out
        if (this.zones) {
          this.zones.update(dt);
          if (this.state !== 'playing') break;         // knockout / overtime decided it
          if (this.zones.overtime) break;              // the clock stays at 0 through overtime
        }
        if (this.overtime) break;                      // sudden death: the clock stays at 0 until someone scores
        this.time -= dt;
        if (!this.attract) {
          if (!this.lastMinuteFired && this.time <= 60 && this.duration > 60) { this.lastMinuteFired = true; emit('match:oneminute', {}); }
          if (!this.thirtySecFired && this.time <= 30 && this.duration > 30) { this.thirtySecFired = true; emit('match:thirtysec', {}); }
          const c = Math.ceil(this.time);
          if (this.time <= MATCH.finalCountdown && c !== this.lastCount && c > 0) { this.lastCount = c; emit('match:count', { n: c }); }
        }
        if (this.time <= 0) {
          this.time = 0;
          if (!this.follower && !this.zones && this._startOvertime()) break;
          // Zone Control may go to overtime instead; online, the host calls time
          if (!this.follower && (!this.zones || this.zones.timeUp())) { if (this.state === 'playing') this.setState('finish'); }
        }
        break;
      }
      case 'finish':
        if (this.stateT > (this.bossMode ? (this.bossMode.boss.dead ? BOSS_MODE.finishWin : BOSS_MODE.finishLose) : 2.6) && !this.follower && !this.result) this._judge();
        break;
    }
    // actors (the local controller runs once per rendered frame via updateController)
    const live = this.state === 'playing';
    for (const a of this.actors) {
      if (a.bot) {
        if (live) a.bot.update(dt);
        else { a.intent.move.set(0, 0, 0); a.intent.fire = a.intent.squid = a.intent.sub = a.intent.jump = a.intent.special = false; }
      }
    }
    const nm = G.netm;
    for (const a of this.actors) { if (a.remote && nm) nm.applyRemote(a, dt); else a.update(dt); }
    this.bossMode?.update(dt);
    // soft push between actors
    for (let i = 0; i < this.actors.length; i++) for (let j = i + 1; j < this.actors.length; j++) {
      const a = this.actors[i], b = this.actors[j];
      if (!a.alive || !b.alive) continue;
      const dx = b.pos.x - a.pos.x, dz = b.pos.z - a.pos.z, dy = b.pos.y - a.pos.y;
      const d2 = dx * dx + dz * dz;
      const r = PLAYER.radius * 1.7;
      if (d2 < r * r && Math.abs(dy) < 1.2 && d2 > 1e-5) {
        // online: other players' slime dualists are where their owners say — only your own side gives way
        const ka = a.remote ? 0 : b.remote ? 1 : 0.5, kb = b.remote ? 0 : a.remote ? 1 : 0.5;
        const d = Math.sqrt(d2), push = (r - d);
        a.pos.x -= (dx / d) * push * ka; a.pos.z -= (dz / d) * push * ka;
        b.pos.x += (dx / d) * push * kb; b.pos.z += (dz / d) * push * kb;
      }
    }
  }

  // Duel / Deathmatch tied at the buzzer: sudden death. Returns true if overtime started.
  _startOvertime() {
    if (this.overtime) return false;
    if (this.mode === 'duel') {
      const [a, b] = this.actors;
      if (!a || !b || (a.stats?.splats || 0) !== (b.stats?.splats || 0)) return false;
    } else if (this.mode === 'deathmatch') {
      const top = Math.max(...this.actors.map((a) => a.stats?.splats || 0));
      const tied = this.actors.filter((a) => (a.stats?.splats || 0) === top);
      if (tied.length < 2) return false;
      this.tiedPlayers = new Set(tied);
    } else return false;
    this.overtime = true;
    emit('match:overtime', { mode: this.mode });
    G.audio?.play?.('zone_overtime', { volume: 0.8 });
    return true;
  }

  updateController(dt) {
    if (!this.controller) return;
    this.controller.enabled = this.state === 'playing' && !this.paused && this.local.alive;
    this.controller.update(dt);
  }
  _getDeathmatchSpawns(count = 8) {
    const spawns = [];
    const pads = G.level?.spawnPads || [];
    // only nodes on solid deck with deck all round (a node on a pier's edge or over the water would drop you in the sea)
    const navNodes = (G.nav?.nodes || []).filter((n) => (G.nav?.validIds ? G.nav.validIds.includes(n.id) : true) && this._dmSpawnOk(n.x, n.y, n.z));

    if (pads[0]) spawns.push({ x: pads[0].x, y: pads[0].y, z: pads[0].z, yaw: 0 });
    if (pads[1]) spawns.push({ x: pads[1].x, y: pads[1].y, z: pads[1].z, yaw: Math.PI });

    if (navNodes.length > 0) {
      while (spawns.length < count) {
        let bestNode = null;
        let maxMinDist = -1;
        const step = Math.max(1, Math.floor(navNodes.length / 100));
        for (let i = 0; i < navNodes.length; i += step) {
          const n = navNodes[i];
          let minDist = Infinity;
          for (const sp of spawns) {
            const d = Math.hypot(n.x - sp.x, n.z - sp.z);
            if (d < minDist) minDist = d;
          }
          if (minDist > maxMinDist) {
            maxMinDist = minDist;
            bestNode = n;
          }
        }
        if (bestNode && maxMinDist > 5) {
          const yaw = Math.atan2(-bestNode.x, -bestNode.z);
          spawns.push({ x: bestNode.x, y: bestNode.y, z: bestNode.z, yaw });
        } else {
          break;
        }
      }
    }

    // too few nodes: try rings round the middle, keeping only points on safe deck
    const cy = pads[0]?.y ?? 0.5;
    for (const r of [22, 16, 10, 28]) {
      for (let k = 0; k < 16 && spawns.length < count; k++) {
        const ang = (k / 16) * Math.PI * 2, x = Math.cos(ang) * r, z = Math.sin(ang) * r;
        const y = G.level ? G.level.groundHeight(x, z, cy + 6) : cy;
        if (!this._dmSpawnOk(x, y, z) || spawns.some((s) => Math.hypot(s.x - x, s.z - z) < 5)) continue;
        spawns.push({ x, y, z, yaw: ang + Math.PI });
      }
    }
    // still short: the safe points get shared (pickSafeDeathmatchSpawn spreads people over them)
    for (let i = 0, n = spawns.length; n && spawns.length < count; i++) spawns.push({ ...spawns[i % n] });

    return spawns;
  }

  // A deathmatch spawn must stand on solid deck (not the sea), with deck all round it (not on an edge) and nothing
  // overhead within the 4.5 m drop-in (actor.respawn).
  _dmSpawnOk(x, y, z) {
    const L = G.level;
    if (!L) return true;
    const safe = (h) => h > PLAYER.fallDeathY + 0.3;
    const c = L.groundHeight(x, z, y + 1.2);
    if (!safe(c) || Math.abs(c - y) > 1.2) return false;
    if (Math.abs(L.groundHeight(x, z, c + 5.5) - c) > 0.3) return false;
    for (let k = 0; k < 8; k++) {
      const ang = (k / 8) * Math.PI * 2;
      const h = L.groundHeight(x + Math.cos(ang) * 1.3, z + Math.sin(ang) * 1.3, c + 1.2);
      if (!safe(h) || Math.abs(h - c) > 0.9) return false;
    }
    return true;
  }

  pickSafeDeathmatchSpawn(actor) {
    if (!this.deathmatchSpawns || !this.deathmatchSpawns.length) {
      const pad = G.level?.spawnPads?.[actor.team % 2] || { x: 0, y: 1, z: 0 };
      return { x: pad.x, y: pad.y, z: pad.z, yaw: 0 };
    }
    const enemies = (this.actors || []).filter((a) => a !== actor && a.alive);
    if (!enemies.length) {
      return this.deathmatchSpawns[Math.floor(Math.random() * this.deathmatchSpawns.length)];
    }
    let safest = this.deathmatchSpawns[0];
    let maxDistToNearestEnemy = -1;
    for (const sp of this.deathmatchSpawns) {
      let nearest = Infinity;
      for (const e of enemies) {
        const d = Math.hypot(sp.x - e.pos.x, sp.z - e.pos.z);
        if (d < nearest) nearest = d;
      }
      if (nearest > maxDistToNearestEnemy) {
        maxDistToNearestEnemy = nearest;
        safest = sp;
      }
    }
    return safest;
  }


  _judge() {
    try {
      if (this.mode === 'duel') {
        const sorted = [...this.actors].sort((a, b) => 
          ((b.stats?.splats || 0) - (a.stats?.splats || 0)) || 
          ((a.stats?.deaths || 0) - (b.stats?.deaths || 0)) || 
          ((b.stats?.turf || 0) - (a.stats?.turf || 0))
        );
        const winnerActor = sorted[0];
        const isKnockout = sorted.some(a => (a.stats?.splats || 0) >= 5);
        const cov = G.paint?.coverage ? G.paint.coverage() : [0.5, 0.5];
        this.result = {
          mode: 'duel',
          knockout: isKnockout,
          reason: isKnockout ? 'knockout' : 'time',
          coverage: cov,
          winner: winnerActor ? winnerActor.team : 0,
          winnerName: winnerActor ? winnerActor.name : 'Champion',
          rankings: sorted.map((a, i) => ({
            rank: i + 1,
            name: a.name,
            splats: a.stats?.splats || 0,
            deaths: a.stats?.deaths || 0,
            turf: Math.round(a.stats?.turf || 0),
            isLocal: a.isLocal, nid: a.nid,
            color: a.color,
            colorHex: a.colorHex,
            weapon: a.weaponId,
            team: a.team,
          })),
        };
        G.netm?.sendResult(this.result);
        this.setState('judge');
        return;
      }
      if (this.mode === 'deathmatch') {
        const sorted = [...this.actors].sort((a, b) => (b.stats.splats - a.stats.splats) || (a.stats.deaths - b.stats.deaths) || (b.stats.turf - a.stats.turf));
        const winnerActor = sorted[0];
        const cov = G.paint?.coverage ? G.paint.coverage() : [0.5, 0.5];
        this.result = {
          mode: 'deathmatch',
          coverage: cov,
          winner: winnerActor ? winnerActor.team : 0,
          winnerName: winnerActor ? winnerActor.name : 'Champion',
          rankings: sorted.map((a, i) => ({
            rank: i + 1,
            name: a.name,
            splats: a.stats.splats,
            deaths: a.stats.deaths,
            turf: Math.round(a.stats.turf),
            isLocal: a.isLocal, nid: a.nid,
            color: a.color, colorHex: a.colorHex,
            weapon: a.weaponId,
          })),
        };
        G.netm?.sendResult(this.result);
        this.setState('judge');
        return;
      }
      if (this.zones) {
        const Z = this.zones;
        const cov = G.paint?.coverage ? G.paint.coverage() : [0.5, 0.5];
        const win = Z.winner != null ? Z.winner : (Z.count[0] === Z.count[1] ? (Math.random() < 0.5 ? 0 : 1) : Z.count[0] < Z.count[1] ? 0 : 1);
        this.result = { mode: 'zones', coverage: cov, winner: win, reason: Z.reason || 'time',
          counts: [Math.ceil(Z.count[0] ?? 100), Math.ceil(Z.count[1] ?? 100)], penalty: [...(Z.penalty || [0, 0])], overtime: !!Z.overtime, log: Z.log || [] };
        G.netm?.sendResult(this.result);        // online: every client shows the host's result
        this.setState('judge');
        return;
      }
      if (this.bossMode) {
        this.result = this.bossMode.result();
        G.netm?.sendResult(this.result);
        this.setState('judge');
        return;
      }
      const cov = G.paint?.coverage ? G.paint.coverage() : [0.5, 0.5];
      const win = cov[0] === cov[1] ? (Math.random() < 0.5 ? 0 : 1) : cov[0] > cov[1] ? 0 : 1;
      this.result = { coverage: cov, winner: win };
      G.netm?.sendResult(this.result);        // online: every client shows the host's count
      this.setState('judge');
    } catch (e) {
      console.error('[match] error in _judge:', e);
      this.result = { coverage: [0.5, 0.5], winner: 0 };
      this.setState('judge');
    }
  }

  teamSummary() {
    if (this.mode === 'duel') {
      return [0, 1].map((t) => {
        const a = this.actors.find((act) => act.team === t);
        return {
          color: G.teamHex?.[t] || (t === 0 ? '#ff8a14' : '#2f5bff'),
          players: a ? [{
            name: a.name, weapon: a.weaponId, alive: a.alive, respawn: a.alive ? 0 : Math.max(0, a.respawnTimer),
            specialReady: a.specialReady(), isSelf: a.isLocal, colorHex: a.colorHex,
            team: a.team, slot: a.slot, splats: a.stats?.splats || 0,
          }] : [],
        };
      });
    }
    if (this.mode === 'deathmatch') {
      const pList = this.actors.map((a) => ({
        name: a.name, weapon: a.weaponId, alive: a.alive, respawn: a.alive ? 0 : Math.max(0, a.respawnTimer),
        specialReady: a.specialReady(), isSelf: a.isLocal, colorHex: a.colorHex,
        team: a.team, slot: a.slot,
      }));
      return [
        { color: G.teamHex?.[0] || '#a6f01a', players: pList.slice(0, 4) },
        { color: G.teamHex?.[4] || '#8a3cff', players: pList.slice(4, 8) },
      ];
    }
    return [0, 1].map((t) => ({
      color: G.teamHex[t],
      players: this.actors.filter((a) => a.team === t).map((a) => ({
        name: a.name, weapon: a.weaponId, alive: a.alive, respawn: a.alive ? 0 : Math.max(0, a.respawnTimer), specialReady: a.specialReady(), isSelf: a.isLocal,
      })),
    }));
  }
}

// a point on (or just above) a zone: inside one of its outlines, near its floor heights
function inZone(def, p) {
  if (p.y < (def.y0 ?? -2) - 1 || p.y > (def.y1 ?? 6) + 2.5) return false;
  for (const poly of def.polys || [def.poly]) {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const [xi, zi] = poly[i], [xj, zj] = poly[j];
      if ((zi > p.z) !== (zj > p.z) && p.x < ((xj - xi) * (p.z - zi)) / (zj - zi) + xi) inside = !inside;
    }
    if (inside) return true;
  }
  return false;
}

function shuffle(a) { for (let i = a.length - 1; i > 0; i--) { const j = (Math.random() * (i + 1)) | 0; [a[i], a[j]] = [a[j], a[i]]; } return a; }
